import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { MAN_VS_MACHINE_REGISTRATION_PATH } from '../../routes/ManVsMachineCheckoutRoutes'
import useFetchUserByCheckoutSessionId from '../../api/onboarding/useFetchUserByCeckoutSessionId'
import { t } from 'i18next'
import { AnimatePresence, motion } from 'framer-motion'
import ManVsMachineCheckoutLayout from './ManVsMachineCheckoutLayout'

const bounceKeyframes = {
	'@keyframes bounce': {
		'0%, 100%': { transform: 'translateY(0)' },
		'10%': { transform: 'translateY(-8px)' },
		'20%': { transform: 'translateY(0)' },
	},
}

interface WelcomeAnimationProps {
	showWelcomeTitle: boolean
	showBullet1: boolean
	showBullet2: boolean
	showBullet3: boolean
	showContinueButton: boolean
	onContinue: () => void
	palette: any
	hasError: boolean
}

const WelcomeAnimation: React.FC<WelcomeAnimationProps> = ({
	showWelcomeTitle,
	showBullet1,
	showBullet2,
	showBullet3,
	showContinueButton,
	onContinue,
	palette,
	hasError,
}) => {
	return (
		<Box
			sx={{
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				opacity: showWelcomeTitle ? 1 : 0,
				transform: showWelcomeTitle ? 'translateY(0)' : 'translateY(20px)',
				transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
				zIndex: 10,
				pointerEvents: 'auto',
			}}
		>
			<Stack alignItems="flex-start" gap={4} sx={{ maxWidth: '680px', width: '100%', mx: 'auto' }}>
				<Box
					textAlign="center"
					sx={{
						maxWidth: '800px',
						width: '100%',
						mx: 'auto',
					}}
				>
					<Typography
						variant="h1"
						sx={{
							fontFamily: '"Eczar", serif',
							fontSize: { xs: '32px', md: '48px' },
							color: palette.text.darkBlue,
							fontWeight: '500',
							mb: 2,
						}}
					>
						Welcome to Man vs. Machine
					</Typography>
					<Typography
						variant="h2"
						sx={{
							fontSize: { xs: '18px', md: '24px' },
							color: palette.text.secondary,
							fontFamily: '"Eczar", serif',
							fontWeight: '400',
							mb: 2,
						}}
					>
						You just made an awesome decision.
					</Typography>
				</Box>

				{/* Bullet points section – replace centered stack with intro paragraph + left-aligned bullets + closing line */}
				{/* Intro paragraph */}
				<Typography
					sx={{
						fontSize: '16px',
						textAlign: 'left',
						mb: 2,
						// no explicit maxWidth so it inherits container width
					}}
				>
					Over the next 12 months you’ll work side-by-side with investors who already turn cold calls into
					signed contracts. Fueled by Prop.ai’s battle-tested automations.
				</Typography>

				{/* Bullet points */}
				<Stack gap={2} sx={{ maxWidth: '800px', textAlign: 'left', pl: 2, width: '100%' }}>
					{/* Bullet 1 */}
					<Typography
						sx={{
							fontSize: '16px',
							opacity: showBullet1 ? 1 : 0,
							transform: showBullet1 ? 'translateX(0)' : 'translateX(-20px)',
							transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
							transitionDelay: '0.2s',
						}}
					>
						<b>90-Day Speed Run:</b> plug into proven AI models for cold-call campaigns, underwriting, and
						follow-up.
					</Typography>

					{/* Bullet 2 */}

					<Typography
						sx={{
							fontSize: '16px',
							opacity: showBullet2 ? 1 : 0,
							transform: showBullet2 ? 'translateX(0)' : 'translateX(-20px)',
							transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
							transitionDelay: '0.2s',
						}}
					>
						<b>Nationwide Deal Squad:</b> match with closers and buyers ready to fund or flip the leads you
						surface.
					</Typography>

					{/* Bullet 3 */}

					<Typography
						sx={{
							fontSize: '16px',
							opacity: showBullet3 ? 1 : 0,
							transform: showBullet3 ? 'translateX(0)' : 'translateX(-20px)',
							transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
							transitionDelay: '0.2s',
						}}
					>
						<b>Earn While You Learn:</b> every contract your team closes puts cash - and confidence - back
						in your pocket.
					</Typography>
				</Stack>

				{/* Continue Button */}
				{hasError ? (
					<Typography
						onClick={hasError ? undefined : onContinue}
						sx={{
							fontSize: '18px',
							opacity: showContinueButton ? 1 : 0,
							transform: showContinueButton ? 'translateX(0)' : 'translateX(-20px)',
							transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
							transitionDelay: '0.2s',
							color: hasError ? palette.text.secondary : palette.brand.main,
							cursor: hasError ? 'default' : 'pointer',
							fontWeight: 'bold',
							textAlign: 'center',
							alignSelf: 'center',
							mt: 2,
							maxWidth: '800px',
							width: '100%',
						}}
					>
						{'Check your email for registration link'}
					</Typography>
				) : (
					<Typography
						onClick={hasError ? undefined : onContinue}
						sx={{
							fontSize: '18px',
							opacity: showContinueButton ? 1 : 0,
							transform: showContinueButton ? 'translateX(0)' : 'translateX(-20px)',
							transition: 'opacity 0.8s ease-in-out, transform 0.8s ease-in-out',
							transitionDelay: '0.2s',
							color: hasError ? palette.text.secondary : palette.brand.main,
							cursor: hasError ? 'default' : 'pointer',
							fontWeight: 'bold',
							textAlign: 'center',
							alignSelf: 'center',
							...bounceKeyframes,
							animation: 'bounce 5s infinite',
							mt: 2,
							maxWidth: '800px',
							width: '100%',
						}}
					>
						{'Click here to set up your account!'}
					</Typography>
				)}
			</Stack>
		</Box>
	)
}

export default function ManVsMachineCheckoutPage() {
	const { palette } = useTheme()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const checkoutSessionId = searchParams.get('session_id')

	const { data: checkoutData, isLoading, error } = useFetchUserByCheckoutSessionId(checkoutSessionId)

	// Animation state
	const [showSecondMessage, setShowSecondMessage] = useState(false)

	// Welcome animation states
	const [showWelcomeTitle, setShowWelcomeTitle] = useState(false)
	const [showBullet1, setShowBullet1] = useState(false)
	const [showBullet2, setShowBullet2] = useState(false)
	const [showBullet3, setShowBullet3] = useState(false)
	const [showContinueButton, setShowContinueButton] = useState(false)

	// Loading animation effect
	useEffect(() => {
		if (isLoading) {
			const timer = setTimeout(() => {
				setShowSecondMessage(true)
			}, 3000)
			return () => clearTimeout(timer)
		} else {
			setShowSecondMessage(false)
		}
	}, [isLoading])

	// Welcome animation sequence
	useEffect(() => {
		// Start welcome animation sequence immediately
		const timer1 = setTimeout(() => setShowWelcomeTitle(true), 500)
		const timer2 = setTimeout(() => setShowBullet1(true), 2000)
		const timer3 = setTimeout(() => setShowBullet2(true), 3500)
		const timer4 = setTimeout(() => setShowBullet3(true), 5000)
		const timer5 = setTimeout(() => setShowContinueButton(true), 6500) // Show continue button

		return () => {
			clearTimeout(timer1)
			clearTimeout(timer2)
			clearTimeout(timer3)
			clearTimeout(timer4)
			clearTimeout(timer5)
		}
	}, []) // Only run once when component mounts

	const handleContinue = () => {
		// Navigate to the registration page with the checkout data in state
		const params = new URLSearchParams()
		if (checkoutSessionId) {
			params.set('session_id', checkoutSessionId)
		}
		navigate(`${MAN_VS_MACHINE_REGISTRATION_PATH}?${params.toString()}`, {
			state: { checkoutData },
		})
	}

	// Loading state with welcome animation
	if (isLoading) {
		return (
			<Box
				sx={{
					position: 'relative',
					height: '100vh',
					overflow: 'hidden',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<WelcomeAnimation
					showWelcomeTitle={showWelcomeTitle}
					showBullet1={showBullet1}
					showBullet2={showBullet2}
					showBullet3={showBullet3}
					showContinueButton={showContinueButton && !isLoading} // Fixed loading reference
					onContinue={handleContinue}
					palette={palette}
					hasError={!!error}
				/>
			</Box>
		)
	}

	// Show welcome animation
	return (
		<Box
			sx={{
				position: 'relative',
				height: '100vh',
				overflow: 'hidden',
			}}
		>
			<WelcomeAnimation
				showWelcomeTitle={showWelcomeTitle}
				showBullet1={showBullet1}
				showBullet2={showBullet2}
				showBullet3={showBullet3}
				showContinueButton={showContinueButton}
				onContinue={handleContinue}
				palette={palette}
				hasError={!!error}
			/>
		</Box>
	)
}
